/**
 * HalachicCalendarGrid — 7-column calendar grid component.
 *
 * Displays a month grid with Gregorian + Hebrew date subscript labels,
 * color-coded availability, WCAG AA keyboard navigation, and sunset-aware
 * date display.
 *
 * Design system tokens: #2697a2 primary, #28E2A7 success/available,
 * #ff5f5f warning/sponsored, #e5e7eb borders, #404f4d text.
 *
 * RULE: Do not add root `px-*` or `py-*` classes (shell owns insets per CLAUDE.md).
 *
 * Authority: apps/ystnj/documentation/prd/parent-portal/parnes-hayom/v1.1/implementation-plan.md
 */

import { useCallback, useRef } from 'react';
import type { CalendarDayEntry, CampaignCalendarGrid } from '@triedge/domain-core';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type HalachicCalendarGridProps = {
  /** Calendar grid data for the current month. */
  calendarData: CampaignCalendarGrid | null;
  /** Currently selected date (ISO YYYY-MM-DD) or null. */
  selectedDate: string | null;
  /** Called when a date is selected. */
  onDateSelect: (isoDate: string) => void;
  /** Called when navigating to a different month (-1 = prev, +1 = next). */
  onMonthChange: (direction: -1 | 1) => void;
  /** Test ID for Playwright targeting. */
  testId?: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const AVAILABILITY_STYLES: Record<string, string> = {
  available:
    'cursor-pointer bg-white hover:bg-[#28E2A7]/10 border-[#e5e7eb] hover:border-[#28E2A7] text-[#404f4d]',
  sponsored:
    'cursor-default bg-[#ff5f5f]/10 border-[#ff5f5f]/40 text-[#404f4d]/60',
  blackout:
    'cursor-default bg-gray-100 border-[#e5e7eb] text-[#404f4d]/40',
  past:
    'cursor-default bg-gray-50 border-[#e5e7eb] text-[#404f4d]/30',
  'out-of-range':
    'cursor-default bg-gray-50 border-[#e5e7eb] text-[#404f4d]/20',
};

const SELECTED_STYLE =
  'ring-2 ring-[#2697a2] border-[#2697a2] bg-[#2697a2]/5';

const TODAY_INDICATOR = 'ring-1 ring-[#2697a2]/40';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HalachicCalendarGrid({
  calendarData,
  selectedDate,
  onDateSelect,
  onMonthChange,
  testId = 'halachic-calendar-grid',
}: HalachicCalendarGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, day: CalendarDayEntry | null) => {
      if (!day || day.availability !== 'available') return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onDateSelect(day.gregorianDate);
      }
    },
    [onDateSelect]
  );

  if (!calendarData) {
    return (
      <div
        data-testid={testId}
        className="animate-pulse rounded-xl border border-[#e5e7eb] bg-white"
        role="status"
        aria-label="Loading calendar"
      >
        <div className="space-y-3 p-6">
          <div className="h-6 w-48 rounded bg-gray-200" />
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="h-16 rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-[#e5e7eb] bg-white shadow-sm"
    >
      {/* Month Header + Navigation */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] p-4">
        <button
          type="button"
          onClick={() => onMonthChange(-1)}
          className="rounded-lg p-2 text-[#404f4d] transition-colors hover:bg-gray-100"
          aria-label="Previous month"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h3 className="font-heading text-lg font-semibold text-[#404f4d]">
          {calendarData.monthLabel}
        </h3>

        <button
          type="button"
          onClick={() => onMonthChange(1)}
          className="rounded-lg p-2 text-[#404f4d] transition-colors hover:bg-gray-100"
          aria-label="Next month"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Availability Legend */}
      <div className="flex flex-wrap items-center gap-4 border-b border-[#e5e7eb] px-4 py-2 text-xs text-[#404f4d]/70">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-[#28E2A7] bg-[#28E2A7]/20" />
          Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-[#ff5f5f]/40 bg-[#ff5f5f]/10" />
          Sponsored
        </span>
        <span className="text-[#404f4d]/50">
          {calendarData.availableCount} available / {calendarData.sponsoredCount} sponsored
        </span>
      </div>

      {/* Day-of-Week Headers */}
      <div className="grid grid-cols-7 border-b border-[#e5e7eb]">
        {DAY_LABELS.map((label) => (
          <div
            key={label}
            className="py-2 text-center text-xs font-medium text-[#404f4d]/60"
            aria-hidden="true"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="source-faithful-tableScroll">
      <div
        ref={gridRef}
        className="grid min-w-[560px] grid-cols-7 gap-px bg-[#e5e7eb]/50 p-px"
        role="grid"
        aria-label={`${calendarData.monthLabel} calendar`}
      >
        {calendarData.days.map((day, index) => {
          if (!day) {
            return <div key={`pad-${index}`} className="bg-white" role="gridcell" aria-hidden="true" />;
          }

          const isSelected = day.gregorianDate === selectedDate;
          const isClickable = day.availability === 'available';
          const baseStyle = AVAILABILITY_STYLES[day.availability] ?? AVAILABILITY_STYLES.past;

          return (
            <div
              key={day.gregorianDate}
              role="gridcell"
              tabIndex={isClickable ? 0 : -1}
              aria-selected={isSelected}
              aria-disabled={!isClickable}
              aria-label={`${day.hebrewDate.hebrewDateEnglish}, ${day.gregorianDate}${
                day.availability === 'sponsored' ? ', sponsored' : ''
              }${day.availability === 'blackout' ? ', unavailable' : ''}`}
              onClick={isClickable ? () => onDateSelect(day.gregorianDate) : undefined}
              onKeyDown={(e) => handleKeyDown(e, day)}
              className={[
                'relative flex min-h-[4rem] flex-col items-center justify-center border bg-white transition-all',
                baseStyle,
                isSelected ? SELECTED_STYLE : '',
                day.isToday && !isSelected ? TODAY_INDICATOR : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {/* Gregorian day number */}
              <span className="text-sm font-medium leading-none">
                {day.dayOfMonth}
              </span>

              {/* Hebrew date subscript */}
              <span className="mt-0.5 text-[10px] leading-tight opacity-70" dir="rtl">
                {day.hebrewDate.hebrewDateLabel}
              </span>

              {/* Availability indicator dot */}
              {day.availability === 'available' ? (
                <span className="absolute bottom-1 h-1 w-1 rounded-full bg-[#28E2A7]" aria-hidden="true" />
              ) : null}
              {day.availability === 'sponsored' ? (
                <span className="absolute bottom-1 h-1 w-1 rounded-full bg-[#ff5f5f]" aria-hidden="true" />
              ) : null}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
